import React, { useState } from 'react';
import { Button, Modal } from 'equasion-lib';
import { normalizeExpirationInput } from '@/lib/scanning/retailCode';

export type PickManualUnitFields = { code: string; expiration: string | null };

type PickManualEntryProps = {
  /** Step 1 types the zone name; step 2 types the unit's product code (+ optional expiration). */
  mode: 'zone' | 'unit';
  kind: string;
  productName: string;
  expectedZoneName: string;
  /** A lookup / confirm is in flight — the submit waits for it. */
  busy: boolean;
  onSubmitZone: (text: string) => void;
  onSubmitUnit: (fields: PickManualUnitFields) => void;
  onClose: () => void;
};

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

/**
 * The Pick sheet's manual entry, in the same modal every other scanner screen uses. The page still
 * owns when it closes: the step machine unmounts it the moment the typed value is accepted, and
 * leaves it open, with what was typed, when it is refused.
 *
 * Typed values go through exactly the checks a scan does — the zone name through the same
 * `zoneByScanCode` lookup, the product code through `salesOrderPickConfirmLine` — so manual entry is
 * a way around a torn label, never around the rules. For a serialized line the expiration may be
 * typed from the pack (never pre-filled) and is kept with the pick; it is optional, as Pick accepts
 * the unit by zone and product alone.
 */
function PickManualEntry({
  mode,
  kind,
  productName,
  expectedZoneName,
  busy,
  onSubmitZone,
  onSubmitUnit,
  onClose
}: PickManualEntryProps) {
  const [zoneText, setZoneText] = useState('');
  const [code, setCode] = useState('');
  const [expiration, setExpiration] = useState('');
  const serialized = kind === 'SERIALIZED';
  const normalizedExpiration = normalizeExpirationInput(expiration);
  const zoneReady = !!zoneText.trim();
  const unitReady = !!code.trim();

  const submitZone = () => {
    if (!busy && zoneReady) onSubmitZone(zoneText.trim());
  };
  const submitUnit = () => {
    if (!busy && unitReady)
      onSubmitUnit({ code: code.trim(), expiration: normalizedExpiration });
  };

  return (
    <Modal
      className="pick-manual-modal"
      modalOpen
      setModalOpen={(nextOpen?: boolean) => {
        if (!nextOpen) onClose();
      }}
    >
      <form
        className="pick-manual"
        onSubmit={(e) => {
          e.preventDefault();
          if (mode === 'zone') submitZone();
          else submitUnit();
        }}
      >
        <strong>
          {mode === 'zone' ? 'Type the zone name' : 'Type the product code'}
        </strong>
        {mode === 'zone' ? (
          <>
            <p>
              For a label that will not scan. Suggested zone:{' '}
              <strong>{expectedZoneName}</strong> — any zone holding this product is OK.
            </p>
            <label className="pick-manual__field">
              <span>Zone name</span>
              <input
                name="zoneName"
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
                value={zoneText}
                onChange={(e) => setZoneText(e.target.value)}
              />
            </label>
          </>
        ) : (
          <>
            <p>
              The NDC, UPC or GTIN printed on one unit of <strong>{productName}</strong>
              {serialized ? ', and its expiration date if you can read it.' : '.'}
            </p>
            <label className="pick-manual__field">
              <span>NDC / UPC / GTIN</span>
              <input
                name="code"
                autoFocus
                autoComplete="off"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            {serialized && (
              <label className="pick-manual__field">
                <span>Expiration date (optional)</span>
                <input
                  name="expiration"
                  type="date"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </label>
            )}
          </>
        )}
        <Button
          type="primary"
          fullWidth
          isSubmit
          disabled={busy || (mode === 'zone' ? !zoneReady : !unitReady)}
        >
          {busy ? 'Checking…' : mode === 'zone' ? 'Check zone' : 'Check unit'}
        </Button>
        <Button type="secondary" fullWidth {...click(onClose)}>
          Cancel
        </Button>
      </form>
    </Modal>
  );
}

export default PickManualEntry;
